/**
 * Cursor management for pagination
 * This system stores cursor positions in localStorage to enable SEO-friendly page URLs
 * while using GraphQL cursor-based pagination under the hood.
 */

/**
 * Generates the storage key for storing cursors for a specific author.
 *
 * @param authorSlug - The slug of the author.
 * @returns A string representing the storage key for the author's cursors.
 */
export const getCursorStorageKey = (authorSlug: string): string => `author_cursors_${authorSlug}`;

/**
 * Retrieves the cursor for a specific page number for a given author.
 *
 * @param authorSlug - The slug of the author.
 * @param pageNumber - The page number to retrieve the cursor for.
 * @returns The cursor string for the specified page, or null if not found.
 */
export const getCursorForPage = (authorSlug: string, pageNumber: number): string | null => {
    if (typeof window === 'undefined') return null;

    const storageKey = getCursorStorageKey(authorSlug);
    const cursors = JSON.parse(localStorage.getItem(storageKey) || '{}');
    return cursors[pageNumber] || null;
};

/**
 * Saves the cursor for a specific page number for a given author.
 *
 * How it works:
 * 1. Page 1: Start with no cursor (beginning of list)
 * 2. When Page 1 loads, save its endCursor for Page 2
 * 3. Page 2: Use saved cursor from Page 1's endCursor
 * 4. When Page 2 loads, save its endCursor for Page 3
 * And so on...
 *
 * Debug: To clear cursor data, run in console:
 * ```
 * localStorage.removeItem('author_cursors_' + authorSlug)
 * ```
 *
 * @param authorSlug - The slug of the author.
 * @param pageNumber - The page number to save the cursor for.
 * @param cursor - The cursor string to save.
 */
export const saveCursorForPage = (authorSlug: string, pageNumber: number, cursor: string | null): void => {
    if (typeof window === 'undefined') return;

    const storageKey = getCursorStorageKey(authorSlug);
    const cursors = JSON.parse(localStorage.getItem(storageKey) || '{}');
    cursors[pageNumber] = cursor;
    localStorage.setItem(storageKey, JSON.stringify(cursors));
};

/**
 * Finds the nearest cached cursor for a given author slug and page number.
 *
 * This function searches for the highest page number with a cached cursor
 * that is less than the specified current page number.
 *
 * @param authorSlug - The slug of the author.
 * @param currentPageNumber - The current page number.
 * @returns An object containing the nearest page number and its cursor.
 */
export const findNearestCachedCursor = (
    authorSlug: string,
    currentPageNumber: number,
): { nearestPage: number; cursor: string | null } => {
    if (typeof window === 'undefined') return { nearestPage: 0, cursor: null };

    const storageKey = getCursorStorageKey(authorSlug);
    const cursors = JSON.parse(localStorage.getItem(storageKey) || '{}');

    let nearestPage = 0;
    let nearestCursor = null;

    // Find the highest page number we have a cursor for that's less than currentPageNumber
    for (let page = 1; page < currentPageNumber; page++) {
        if (cursors[page + 1]) {
            // cursor[page + 1] represents the cursor AFTER page
            nearestPage = page;
            nearestCursor = cursors[page + 1];
        }
    }

    return { nearestPage, cursor: nearestCursor };
};

export default { getCursorForPage, getCursorStorageKey, saveCursorForPage };
